import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, Container, FormControlLabel, Paper,
    Radio, RadioGroup, Stack, Table, TableBody, TableCell, TableContainer,
    TableHead, TablePagination, TableRow, TextField, Toolbar, Typography,
} from '@mui/material';
import Appbar from '../../components/Appbar';
import { ExternalCatalogIntegration } from '../../types/externalCatalog';
import {
    DiagnosticLab, DiagnosticLocation, DiagnosticPackageOption, DiagnosticServiceabilityResult,
    fetchDiagnosticPackages, fetchDiagnosticServiceability, parseDiagnosticLocation,
} from '../../services/serviceability';

const providers: { id: ExternalCatalogIntegration; name: string }[] = [
    { id: 'labstack', name: 'Labstack' },
    { id: 'healthians', name: 'Healthians' },
    { id: 'betacura', name: 'BetaCura' },
];

const visitTypes: Record<string, string> = {
    HOME_SAMPLE: 'Home collection', CENTER_VISIT: 'Centre visit', HYBRID: 'Hybrid',
};

const distanceOptions = [5, 10, 25, 50, 100];

const providerName = (integration: ExternalCatalogIntegration) =>
    providers.find(item => item.id === integration)?.name || integration;

function LabsTable({ items, filter }: { items: DiagnosticLab[]; filter: string }) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    useEffect(() => setPage(0), [items, filter]);
    const term = filter.trim().toLowerCase();
    const rows = items.filter(item => [item.id, item.name, item.address, item.city, item.state, item.pincode]
        .some(value => String(value || '').toLowerCase().includes(term)));
    const visiblePage = Math.min(page, Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1));

    return (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" component="h2">Nearby labs ({rows.length})</Typography>
            </Box>
            {rows.length === 0 ? <Typography color="text.secondary" sx={{ p: 2 }}>
                {filter.trim() ? 'No labs match your filter.' : 'No nearby labs were returned for this package and location.'}
            </Typography> : <>
                <TableContainer>
                    <Table size="small" aria-label="Nearby labs">
                        <TableHead><TableRow>
                            <TableCell>Lab</TableCell><TableCell>Address</TableCell>
                            <TableCell>Visit type</TableCell><TableCell>Coordinates</TableCell>
                            <TableCell align="right">Distance</TableCell>
                        </TableRow></TableHead>
                        <TableBody>{rows.slice(visiblePage * rowsPerPage, (visiblePage + 1) * rowsPerPage).map(item =>
                            <TableRow key={item.id} hover>
                                <TableCell><Typography variant="body2" fontWeight={600}>{item.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">{item.id}</Typography>
                                </TableCell>
                                <TableCell>{[item.address, item.city, item.state, item.pincode].filter(Boolean).join(', ') || '—'}</TableCell>
                                <TableCell>{visitTypes[item.packageType || ''] || 'Not specified'}</TableCell>
                                <TableCell>{item.latitude !== undefined && item.longitude !== undefined
                                    ? `${item.latitude.toFixed(5)}, ${item.longitude.toFixed(5)}` : '—'}</TableCell>
                                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                    {item.distanceKm === undefined ? '—' : `${item.distanceKm.toFixed(2)} km`}
                                </TableCell>
                            </TableRow>)}</TableBody>
                    </Table>
                </TableContainer>
                <TablePagination component="div" count={rows.length} page={visiblePage} rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[10, 25, 50]} onPageChange={(_, next) => setPage(next)}
                    onRowsPerPageChange={event => { setRowsPerPage(Number(event.target.value)); setPage(0); }} />
            </>}
        </Paper>
    );
}

type CheckedResult = {
    integration: ExternalCatalogIntegration;
    packageOption: DiagnosticPackageOption;
    location: DiagnosticLocation;
    data: DiagnosticServiceabilityResult;
};

export default function Serviceability() {
    const [integration, setIntegration] = useState<ExternalCatalogIntegration | ''>('');
    const [packages, setPackages] = useState<DiagnosticPackageOption[]>([]);
    const [packageId, setPackageId] = useState('');
    const [packagesLoading, setPackagesLoading] = useState(false);
    const [packageError, setPackageError] = useState('');
    const [mode, setMode] = useState('pincode');
    const [pincode, setPincode] = useState('');
    const [latitude, setLatitude] = useState('');
    const [longitude, setLongitude] = useState('');
    const [validationError, setValidationError] = useState('');
    const [loading, setLoading] = useState(false);
    const [checkError, setCheckError] = useState('');
    const [checked, setChecked] = useState<CheckedResult | null>(null);
    const [filter, setFilter] = useState('');
    const [maxDistanceKm, setMaxDistanceKm] = useState(10);

    useEffect(() => {
        if (!integration) {
            setPackages([]);
            setPackageId('');
            return;
        }
        let active = true;
        setPackagesLoading(true);
        setPackageError('');
        setPackages([]);
        setPackageId('');
        setChecked(null);
        fetchDiagnosticPackages(integration)
            .then(data => { if (active) setPackages(data); })
            .catch(error => { if (active) setPackageError(error.message); })
            .finally(() => { if (active) setPackagesLoading(false); });
        return () => { active = false; };
    }, [integration]);

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!integration) return setValidationError('Choose a vendor.');
        const packageOption = packages.find(item => item.id === packageId);
        if (!packageOption) return setValidationError('Choose a package.');
        try {
            const location = parseDiagnosticLocation(mode, pincode, latitude, longitude);
            setValidationError('');
            setCheckError('');
            setChecked(null);
            setFilter('');
            setMaxDistanceKm(10);
            setLoading(true);
            const data = await fetchDiagnosticServiceability(integration, packageId, location);
            setChecked({ integration, packageOption, location, data });
        } catch (error) {
            const message = (error as Error).message;
            if (message.startsWith('Enter ')) setValidationError(message);
            else setCheckError(message);
        } finally {
            setLoading(false);
        }
    };

    const locationDisabled = !packageId || loading;
    const hasDistance = Boolean(checked?.data.labs.some(lab => lab.distanceKm !== undefined));
    const visibleLabs = checked?.data.labs.filter(lab =>
        !hasDistance || maxDistanceKm === 0 ||
        (lab.distanceKm !== undefined && lab.distanceKm <= maxDistanceKm)
    ) || [];

    return (
        <Box sx={{ display: 'flex' }}>
            <Appbar appBarTitle="Serviceability" />
            <Box component="main" sx={{ flexGrow: 1, minWidth: 0, height: '100vh', overflow: 'auto', bgcolor: 'background.default' }}>
                <Toolbar />
                <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                    <Stack spacing={3}>
                        <Box>
                            <Typography variant="h4" component="h1">Check diagnostic serviceability</Typography>
                            <Typography color="text.secondary" sx={{ maxWidth: 760 }}>
                                Choose one vendor and package, then check coverage for a PIN code or coordinates.
                            </Typography>
                        </Box>

                        <Paper component="form" onSubmit={submit} variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
                            <Stack spacing={2.5}>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                    <TextField select SelectProps={{ native: true }} name="vendor" label="Vendor" value={integration}
                                        onChange={event => { setIntegration(event.target.value as ExternalCatalogIntegration | ''); setValidationError(''); }}
                                        disabled={loading} InputLabelProps={{ shrink: true }} fullWidth>
                                        <option value="">Choose vendor</option>
                                        {providers.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                                    </TextField>
                                    <TextField select SelectProps={{ native: true }} name="package" label="Package" value={packageId}
                                        onChange={event => { setPackageId(event.target.value); setChecked(null); setValidationError(''); }}
                                        disabled={!integration || packagesLoading || loading} InputLabelProps={{ shrink: true }} fullWidth
                                        helperText={packagesLoading ? 'Loading packages…' : integration && !packageError && packages.length === 0 ? 'No enabled packages found.' : ' '}>
                                        <option value="">Choose package</option>
                                        {packages.map(item => <option key={item.id} value={item.id}>{item.name} ({item.id})</option>)}
                                    </TextField>
                                </Stack>
                                {packageError && <Alert severity="error">{packageError}</Alert>}

                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Location</Typography>
                                    <RadioGroup row aria-label="Location search method" value={mode}
                                        onChange={event => { setMode(event.target.value); setChecked(null); setValidationError(''); }}>
                                        <FormControlLabel value="pincode" control={<Radio disabled={locationDisabled} />} label="PIN code" />
                                        <FormControlLabel value="coordinates" control={<Radio disabled={locationDisabled} />} label="Latitude / longitude" />
                                    </RadioGroup>
                                </Box>

                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
                                    {mode === 'pincode' ? <TextField label="PIN code" value={pincode} disabled={locationDisabled}
                                        onChange={event => { setPincode(event.target.value); setChecked(null); }}
                                        InputLabelProps={{ shrink: true }} inputProps={{ inputMode: 'numeric', maxLength: 6 }} placeholder="e.g. 560001" fullWidth /> : <>
                                        <TextField label="Latitude" value={latitude} disabled={locationDisabled}
                                            onChange={event => { setLatitude(event.target.value); setChecked(null); }}
                                            InputLabelProps={{ shrink: true }} inputProps={{ inputMode: 'decimal' }} placeholder="e.g. 12.9716" fullWidth />
                                        <TextField label="Longitude" value={longitude} disabled={locationDisabled}
                                            onChange={event => { setLongitude(event.target.value); setChecked(null); }}
                                            InputLabelProps={{ shrink: true }} inputProps={{ inputMode: 'decimal' }} placeholder="e.g. 77.5946" fullWidth />
                                    </>}
                                    <Button type="submit" variant="contained" disabled={!integration || !packageId || loading}
                                        sx={{ minWidth: 170, height: 56 }}>
                                        {loading ? <CircularProgress size={22} color="inherit" aria-label="Checking serviceability" /> : 'Check availability'}
                                    </Button>
                                </Stack>
                                {validationError && <Alert severity="error">{validationError}</Alert>}
                                {checkError && <Alert severity="error">{checkError}</Alert>}
                            </Stack>
                        </Paper>

                        {!checked ? !loading && <Alert severity="info">
                            Select a vendor and package to check one provider at a time.
                        </Alert> : <Stack spacing={2}>
                            <Box>
                                <Typography component="h2" variant="h6">{checked.packageOption.name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {providerName(checked.integration)} · {checked.location.pincode
                                        ? `PIN ${checked.location.pincode}`
                                        : `${checked.location.latitude}, ${checked.location.longitude}`}
                                </Typography>
                            </Box>
                            <Alert severity={(checked.integration === 'healthians' ? checked.data.serviceable : visibleLabs.length > 0) ? 'success' : 'warning'}>
                                {checked.integration === 'healthians'
                                    ? checked.data.serviceable ? 'Healthians home collection is available at this location.' : 'Healthians does not serve this location.'
                                    : visibleLabs.length > 0 ? `${visibleLabs.length} nearby lab${visibleLabs.length === 1 ? '' : 's'} found.`
                                        : `No labs found${maxDistanceKm > 0 && hasDistance ? ` within ${maxDistanceKm} km` : ''}.`}
                            </Alert>
                            {checked.integration === 'betacura' && <Alert severity="info">
                                {checked.location.pincode
                                    ? 'BetaCura labs are matched by PIN code. Use coordinates when you need calculated distance.'
                                    : 'Distance is calculated in this browser and can be adjusted below.'}
                            </Alert>}
                            {checked.integration !== 'healthians' && <>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <TextField label="Filter labs by name, ID or area" value={filter}
                                        onChange={event => setFilter(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
                                    {hasDistance && <TextField select SelectProps={{ native: true }} name="distance"
                                        label="Maximum distance" value={maxDistanceKm}
                                        onChange={event => setMaxDistanceKm(Number(event.target.value))}
                                        InputLabelProps={{ shrink: true }} sx={{ minWidth: 190 }}>
                                        {distanceOptions.map(distance => <option key={distance} value={distance}>{distance} km</option>)}
                                        <option value={0}>All distances</option>
                                    </TextField>}
                                </Stack>
                                <LabsTable items={visibleLabs} filter={filter} />
                            </>}
                        </Stack>}
                    </Stack>
                </Container>
            </Box>
        </Box>
    );
}
